import { z } from "zod";

import { boxCategorySchema } from "./boxCategories.js";
import {
  LIVE_BREAK_BLOCKED_BY,
  LIVE_BREAK_BOX_INELIGIBLE_REASONS,
  LIVE_BREAK_CLOSE_REASONS,
  LIVE_BREAK_ENDED_BY,
  LIVE_BREAK_ENTRY_STATUSES,
  LIVE_BREAK_MAX_AMOUNT,
  LIVE_BREAK_MAX_BOXES,
  LIVE_BREAK_MAX_RATE_BPS,
  LIVE_BREAK_NOT_ACCEPTING_REASONS,
  LIVE_BREAK_QUEUE_TIERS,
  LIVE_BREAK_RELEASE_REASONS,
  LIVE_BREAK_REVEAL_PHASES,
  LIVE_BREAK_STATUSES,
} from "./liveBreaks.js";
import { CENTS, clientSeedSchema, dropTableItemSchema } from "./primitives.js";
import { liveBreakTwitchStatusSchema } from "./twitch.js";

// ---- live breaks on the wire: the `/v1/live-breaks` views and the realtime event payloads ------
// Boxed's GraphQL types (`LiveBreakRoom`, `LiveBreakQueueEntry`, `LiveBreakOpen`, the stats, host,
// estimate and priority types) in Loot OS's shapes: integer cents, the buyer and the host as public
// profiles, camelCase, and every field always present (null when unset). Boxed's DynamoDB-only
// fields have no counterpart: `allowedBoxIds` is `boxIds`, a room's chat channel is the chat room
// `live-break-{id}` by rule, a queue entry's `boxRefreshedAt` is the pinned `boxVersionId`, and the
// opener style and reveal beat are the contract's constants (`LIVE_BREAK_REVEAL_BEAT_MS`, Decision 20:
// the spinner, for every entry). This file reads nothing from `index.ts`, which re-exports it; the
// two responses that reuse the ledger and claim shapes live there.

// ---- paging and lookup limits ------------------------------------------------------------------

/** Live breaks one directory page lists (boxed's LIVE BREAKS page loads 24 at a time). */
export const LIVE_BREAK_DIRECTORY_PAGE_SIZE = 24;
/** Hosted live breaks one history page lists. */
export const LIVE_BREAK_HISTORY_PAGE_SIZE = 20;
/** Most Twitch logins one by-channel lookup takes (boxed's `LIVE_BREAK_CHANNEL_LOOKUP_LIMIT`). */
export const LIVE_BREAK_CHANNEL_LOOKUP_MAX = 100;
/** Queue entries one queue page lists by default, and at most. */
export const LIVE_BREAK_QUEUE_PAGE_DEFAULT = 50;
export const LIVE_BREAK_QUEUE_PAGE_MAX = 200;
/** Boxes one box search page lists by default, and at most; also the most ids one by-id read names. */
export const LIVE_BREAK_BOX_SEARCH_PAGE_DEFAULT = 24;
export const LIVE_BREAK_BOX_SEARCH_PAGE_MAX = 100;
/** Longest search text the directory and the box search take. */
export const LIVE_BREAK_SEARCH_MAX_LENGTH = 80;

// ---- closed sets the views add -----------------------------------------------------------------

/**
 * Whether a purchase made now joins the Priority tier, and if not why (boxed's
 * `LiveBreakViewerPriorityStatus`): `priority`; `not_subscribed` (Twitch says no);
 * `twitch_unavailable` (Twitch did not answer: a purchase now is standard, worth a retry);
 * `twitch_not_linked`; `twitch_reconnect` (the link is revoked or lacks its scope); `unavailable`
 * (no priority here: closed, the caller hosts it, or it shows no channel).
 */
export const LIVE_BREAK_VIEWER_PRIORITY_STATUSES = [
  "priority",
  "not_subscribed",
  "twitch_unavailable",
  "twitch_not_linked",
  "twitch_reconnect",
  "unavailable",
] as const;
export type LiveBreakViewerPriorityStatus = (typeof LIVE_BREAK_VIEWER_PRIORITY_STATUSES)[number];

/** Which of the caller's entries in one live break a read lists: opened (the results) or queued. */
export const LIVE_BREAK_MY_ENTRY_FILTERS = ["opened", "queued"] as const;
export type LiveBreakMyEntryFilter = (typeof LIVE_BREAK_MY_ENTRY_FILTERS)[number];

const isoTime = z.string().describe("ISO 8601.");
const nullableIsoTime = isoTime.nullable();
const cents = z.int().describe(CENTS);

// ---- people --------------------------------------------------------------------------------------

/**
 * An end user as other shoppers see them in a live break: the host, a buyer, a viewer. The display
 * name is a battle seat's (`endUserDisplayName`: the username, else `Player XXXX`); never an
 * external id or an email. `endUserId` is what `GET /v1/profiles/{endUserId}` reads (a battle
 * seat names the same id `endUser.id`). Boxed's profile picture, banner and level have no Loot OS
 * counterpart.
 */
export const liveBreakProfileSchema = z.object({
  endUserId: z.string(),
  displayName: z.string(),
});
export type LiveBreakProfile = z.infer<typeof liveBreakProfileSchema>;

// ---- the live break --------------------------------------------------------------------------------

/**
 * One live break as everyone reads it (boxed's `LiveBreakRoom` without the per-caller fields): the
 * directory card, the by-channel lookup, the host's open live breaks and the `LIVE_BREAK_UPDATED` and
 * `LIVE_BREAK_CLOSED` events all carry this. Every field is present; unset ones are null.
 */
export const liveBreakSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Listing only: false keeps it off the directory; any live break reads by id. */
  isPublic: z.boolean(),
  /** A house live break: run by the store in Auto Mode, no host, sold at box prices. */
  isHouse: z.boolean(),
  /** The host as a public profile; null on a house live break, which the store runs under its own name. */
  host: liveBreakProfileSchema.nullable(),
  status: z.enum(LIVE_BREAK_STATUSES),
  /** The box categories its live-break boxes are in (games and their families), in list order. */
  categories: z.array(boxCategorySchema),
  /** Its live-break boxes' ids in display order: a changed list is how a viewer learns boxes were added. */
  boxIds: z.array(z.string()),
  /** The host's setting: false = Purchases stopped. Draining stops purchases too (`isDraining`). */
  isAcceptingPurchases: z.boolean(),
  /** Its end has passed: purchases stop and the queue plays out in Auto Mode before it closes. */
  isDraining: z.boolean(),
  /** The host was seen within `LIVE_BREAK_HOST_PRESENCE_MS`; a house live break always is. */
  isHostPresent: z.boolean(),
  /**
   * Why a purchase made now is refused, or null while it takes one: `draining`, `stopped` or
   * `host_away` (`live_break_not_accepting_purchases` names the same). Null once closed too: `status`
   * says that.
   */
  purchaseRefusal: z.enum(LIVE_BREAK_NOT_ACCEPTING_REASONS).nullable(),
  isAutoMode: z.boolean(),
  /** The host's setting (Auto Mode only); inert while Draining or once the host has been away 15 minutes. */
  isPaused: z.boolean(),
  /** True = sells only its live-break boxes; false = any eligible box at or above its minimum box price too. */
  isRestrictedToLiveBreakBoxes: z.boolean(),
  /** Viewers adopt each phase this long after `phaseChangedAt`, to match the stream; the host is exempt. */
  openerDelayMs: z.int(),
  /** Queued entries it takes, every buyer together: a backstop, so `queuedCount` can read a few over. */
  queueCap: z.int(),
  /** Queued entries one buyer may hold here; null = no limit. */
  buyerQueueCap: z.int().nullable(),
  /** The commission rate new purchases pin, in basis points; null on a house live break. */
  commissionRateBps: z.int().nullable(),
  /** The cheapest box, at its own price, that can be added or bought from outside its boxes; 0 = none. */
  minBoxPriceCents: cents,
  /** The live-break price of its cheapest live-break box that can open; null when none can. */
  lowestPriceCents: cents.nullable(),
  /** The Twitch login streamed beside it; null when it shows no stream. */
  twitchChannel: z.string().nullable(),
  /**
   * That channel as the Twitch cache last saw it (live, viewers, name, avatar), display only;
   * null with no channel, without the store's Twitch app, or before anything has read the channel
   * (`GET /v1/twitch/channels` refreshes it, at most once a minute).
   */
  twitchStatus: liveBreakTwitchStatusSchema.nullable(),
  revealPhase: z.enum(LIVE_BREAK_REVEAL_PHASES),
  /** The entry being presented or revealed; null while idle. */
  presentingEntryId: z.string().nullable(),
  /** When the stage entered its phase, on the server clock; null until its first Opening. */
  phaseChangedAt: nullableIsoTime,
  /** When it Drains: End sets it to now; staff may schedule a house live break's. */
  endsAt: nullableIsoTime,
  /** Who pressed End; null for a scheduled end, or before one. */
  endedBy: z.enum(LIVE_BREAK_ENDED_BY).nullable(),
  /** When its host announced it in the site chat; null until then (once per live break). */
  announcedAt: nullableIsoTime,
  closedAt: nullableIsoTime,
  closeReason: z.enum(LIVE_BREAK_CLOSE_REASONS).nullable(),
  /** When a closed live break leaves the directory (closedAt plus `LIVE_BREAK_CLOSED_LISTING_MS`); null while open. */
  listedUntil: nullableIsoTime,
  createdAt: isoTime,
  updatedAt: isoTime,
  /** Entries still queued, the one presenting included (it stays queued until its Reveal). */
  queuedCount: z.int(),
  /** Signed-in viewers whose heartbeat is inside `LIVE_BREAK_VIEWER_WINDOW_MS`: a floor, not the audience. */
  viewerCount: z.int(),
  /**
   * Milliseconds until the server's next phase step, as of `serverTime`: always set while
   * presenting, else only while the server drives the queue and a step is due. Delay it by
   * `openerDelayMs` like every phase change.
   */
  nextStepInMs: z.int().nullable(),
  /** The server's clock when this was read: the anchor for `phaseChangedAt` and `nextStepInMs`. */
  serverTime: isoTime,
});
export type LiveBreak = z.infer<typeof liveBreakSchema>;

/** A live break named from somewhere else (the caller's queued entries across live breaks). */
export const liveBreakRefSchema = liveBreakSchema.pick({
  id: true,
  title: true,
  isHouse: true,
  host: true,
  status: true,
});
export type LiveBreakRef = z.infer<typeof liveBreakRefSchema>;

/**
 * The stage as `SEQUENCE_ADVANCED` carries it: what changes at a phase step and nothing else
 * (boxed's `LiveBreakEventRoomState`), each a complete current value rather than a diff.
 */
export const liveBreakSequenceSchema = liveBreakSchema.pick({
  id: true,
  status: true,
  revealPhase: true,
  presentingEntryId: true,
  phaseChangedAt: true,
  nextStepInMs: true,
});
export type LiveBreakSequence = z.infer<typeof liveBreakSequenceSchema>;

// ---- boxes at live-break prices ----------------------------------------------------------------

/** A box's art beyond `imageUrl`, as a box summary carries it ("" = none). */
export const liveBreakBoxArtSchema = z.object({
  background: z.string(),
  overlay: z.string(),
  mobile: z.object({ image: z.string(), background: z.string(), overlay: z.string() }),
});

/**
 * A box as a live break sells it (boxed's `ListBox` with `liveBreakPricing`): its own price, its
 * live-break price at the rate the read priced it at, and the commission one open earns the host.
 * Prices come from the box's current version, the one a purchase pins, so `priceCents` is what
 * `expectedPriceCents` must name.
 */
export const liveBreakBoxSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  imageUrl: z.string(),
  art: liveBreakBoxArtSchema,
  /** The current version: what a purchase pins and `GET /v1/boxes/{boxId}` serves. */
  version: z.int(),
  categories: z.array(boxCategorySchema),
  /** The box's own price. */
  boxPriceCents: cents,
  /** The live-break price of one box: the own price with the host commission added on top. */
  priceCents: cents,
  /**
   * The host commission one box earns, in whole cents (floored; the host's accruals keep
   * thousandths of a cent). 0 at a rate of 0 or in a house live break.
   */
  commissionPerOpenCents: cents,
  /** Its most valuable item that can drop, for a tile; null when none can. */
  topItem: z
    .object({
      name: z.string(),
      imageUrl: z.string(),
      value: z.int().nonnegative().describe(CENTS),
    })
    .nullable(),
  /**
   * How swingy the current version is: the version's stored `box_versions.volatility`, the number
   * `BoxSummary.volatility` carries, never recomputed. Null when no entry can drop or the version
   * predates it.
   */
  volatility: z
    .number()
    .nonnegative()
    .nullable()
    .describe(
      "How swingy the current version is (the version a purchase pins): one over the chance of rolling an item worth at least the box's expected value, as GET /v1/boxes sends it. Read it on a 0-16 scale in four even levels: low [0, 4), medium [4, 8), high [8, 12), max [12, 16]; the number is sent unclamped, and anything above 16 reads as max. Null when no entry can drop.",
    ),
  /**
   * Why a purchase of it would be refused now (`live_break_box_not_eligible`), or null when it would
   * not: `not_openable`, `excluded_category` (in a box category the store leaves off),
   * `below_min_price` (only from outside the live-break
   * boxes) or `not_in_live_break` (a restricted live break). A box search leaves ineligible boxes
   * out unless it names them by id.
   */
  ineligibleReason: z.enum(LIVE_BREAK_BOX_INELIGIBLE_REASONS).nullable(),
  /** One of the live break's own boxes; false when the read named no live break. */
  isInLiveBreak: z.boolean(),
});
export type LiveBreakBox = z.infer<typeof liveBreakBoxSchema>;

// ---- queue entries -------------------------------------------------------------------------------

/**
 * One box an entry's Reveal rolled, in reveal order (boxed's `LiveBreakOpen`). `openId` reads its
 * proof (`GET /v1/opens/{openId}/verification`); the inventory unit exists once the buyer claims.
 */
export const liveBreakOpenSchema = z.object({
  openId: z.string(),
  nonce: z.int(),
  /** resultNumber in [0, 999999]. */
  result: z.int(),
  itemIndex: z.int(),
  item: dropTableItemSchema,
  /** `getItemRarity(item.value, the entry's priceCents)`: 0 Common … 4 Legendary, the pull strip's glow. */
  rarity: z.int().min(0).max(5),
});
export type LiveBreakOpen = z.infer<typeof liveBreakOpenSchema>;

/**
 * One queue entry as everyone reads it (boxed's `LiveBreakQueueEntry`): the buyer as a public
 * profile, the box and the prices it pinned at purchase, its place while queued and its rolls once
 * opened. The client seed is the buyer's own (`liveBreakMyEntrySchema`). The box's name and image
 * are the box's current ones; Loot OS pins the version, not the copy.
 */
export const liveBreakEntrySchema = z.object({
  id: z.string(),
  liveBreakId: z.string(),
  buyer: liveBreakProfileSchema,
  boxId: z.string(),
  boxName: z.string(),
  boxImageUrl: z.string(),
  /** The version the entry opens against, pinned at purchase, and its number. */
  boxVersionId: z.string(),
  boxVersion: z.int(),
  /** Boxes bought, 1–`LIVE_BREAK_MAX_AMOUNT`, all of `boxId`; presented, opened and cancelled together. */
  amount: z.int().min(1).max(LIVE_BREAK_MAX_AMOUNT),
  /** The box's own price at purchase. */
  boxPriceCents: cents,
  /** The live-break price of one box at purchase: rarity is graded against it. */
  priceCents: cents,
  /** `amount × priceCents`: the hold while queued, the reveal's price once opened. */
  costCents: cents,
  status: z.enum(LIVE_BREAK_ENTRY_STATUSES),
  /** How it got its place (fixed at purchase; a Pick moves it to `next`), not where it sorts. */
  tier: z.enum(LIVE_BREAK_QUEUE_TIERS),
  /** The queue order, compared as strings, ascending; only ever decreases; null once it left the queue. */
  queueKey: z.string().nullable(),
  /** 1-based place among the entries waiting, so Up Next is 1; null on stage and once it left the queue. */
  position: z.int().nullable(),
  /** The open group its Reveal wrote, which its buyer claims or sells back; null until opened. */
  openGroupId: z.string().nullable(),
  /** One per box, in reveal order; null until opened. */
  opens: z.array(liveBreakOpenSchema).nullable(),
  /** Why it left the queue unopened; null unless released. */
  releaseReason: z.enum(LIVE_BREAK_RELEASE_REASONS).nullable(),
  createdAt: isoTime,
  openedAt: nullableIsoTime,
  releasedAt: nullableIsoTime,
});
export type LiveBreakEntry = z.infer<typeof liveBreakEntrySchema>;

/** The reveal an opened entry wrote, as its buyer reads it: claimed or not, and sold back or not. */
export const liveBreakOpenGroupSchema = z.object({
  id: z.string(),
  status: z.enum(["unclaimed", "claimed"]),
  /** What the claim debits (or debited): `amount × priceCents`. */
  costCents: cents,
  claimedAt: nullableIsoTime,
  /** Set once the reveal was sold back: when, and what the sale credited. */
  soldAt: nullableIsoTime,
  soldCents: cents.nullable(),
});
export type LiveBreakOpenGroup = z.infer<typeof liveBreakOpenGroupSchema>;

/** One of the caller's own entries: the public entry plus what only its buyer reads. */
export const liveBreakMyEntrySchema = liveBreakEntrySchema.extend({
  clientSeed: z.string(),
  /** The reveal its opening wrote; null until opened. */
  openGroup: liveBreakOpenGroupSchema.nullable(),
  /**
   * The estimated wait until it is presented, while it waits in the queue (the
   * `GET /v1/live-breaks/{id}/estimate` math); null on stage and once it left the queue.
   */
  waitMs: z.int().nullable(),
});
export type LiveBreakMyEntry = z.infer<typeof liveBreakMyEntrySchema>;

/** One of the caller's queued entries anywhere, with the live break it waits in. */
export const liveBreakMyQueuedEntrySchema = liveBreakMyEntrySchema.extend({
  liveBreak: liveBreakRefSchema,
});
export type LiveBreakMyQueuedEntry = z.infer<typeof liveBreakMyQueuedEntrySchema>;

// ---- stats (computed on read; the host's alone) --------------------------------------------------

/**
 * Spend, opens and host commission, always split (boxed's `LiveBreakRoomStats` figures): viewers'
 * purchases and the host's own are never summed. Spend is at live-break prices; commission is in
 * whole cents, each pool floored on its own.
 */
export const liveBreakStatsTotalsSchema = z.object({
  viewerSpendCents: cents,
  selfSpendCents: cents,
  viewerOpens: z.int(),
  selfOpens: z.int(),
  viewerCommissionCents: cents,
  selfCommissionCents: cents,
});
export type LiveBreakStatsTotals = z.infer<typeof liveBreakStatsTotalsSchema>;

/** One box's spend and opens in a live break, or across all of a host's (boxed's `LiveBreakBoxStats`). */
export const liveBreakBoxStatsSchema = z.object({
  boxId: z.string(),
  boxName: z.string(),
  boxImageUrl: z.string(),
  viewerSpendCents: cents,
  selfSpendCents: cents,
  viewerOpens: z.int(),
  selfOpens: z.int(),
});
export type LiveBreakBoxStats = z.infer<typeof liveBreakBoxStatsSchema>;

/**
 * One of a live break's most valuable pulls (boxed's `LiveBreakTopPull`), graded against what the
 * entry paid. An item that can no longer ship (`isGemSubstitute`) never counts, as boxed leaves its
 * gem prizes out.
 */
export const liveBreakTopPullSchema = z.object({
  entryId: z.string(),
  openId: z.string(),
  /** The box's place in its entry's reveal order, from 0. */
  openIndex: z.int(),
  boxId: z.string(),
  boxName: z.string(),
  boxImageUrl: z.string(),
  /** The live-break price the entry pinned for one box. */
  priceCents: cents,
  item: dropTableItemSchema,
  /** `getItemRarity(item.value, priceCents)`. */
  rarity: z.int().min(0).max(5),
  buyer: liveBreakProfileSchema,
  openedAt: isoTime,
});
export type LiveBreakTopPull = z.infer<typeof liveBreakTopPullSchema>;

/**
 * A live break's stats (boxed's `LiveBreakRoomStats`): the totals, one row per box opened (in
 * first-open order), the #1 box by viewer spend and by viewer opens (null when viewers have none;
 * ties go to the other figure, then the name), and the top `LIVE_BREAK_TOP_PULLS` pulls by item
 * value, then earliest.
 */
export const liveBreakStatsSchema = liveBreakStatsTotalsSchema.extend({
  boxes: z.array(liveBreakBoxStatsSchema),
  topBoxByViewerSpend: liveBreakBoxStatsSchema.nullable(),
  topBoxByViewerOpens: liveBreakBoxStatsSchema.nullable(),
  topPulls: z.array(liveBreakTopPullSchema),
});
export type LiveBreakStats = z.infer<typeof liveBreakStatsSchema>;

// ---- the live break page ---------------------------------------------------------------------------

/**
 * `GET /v1/live-breaks/{id}`: the live break, its boxes at live-break prices, the entry on stage,
 * and the per-caller fields, which come from the session alone and never ride an event.
 */
export const liveBreakDetailSchema = liveBreakSchema.extend({
  /** Its live-break boxes in display order, priced at its rate; a box the environment no longer sees is left out. */
  boxes: z.array(liveBreakBoxSchema),
  /** The entry being presented or revealed (its rolls once revealed); null while idle. */
  presentingEntry: liveBreakEntrySchema.nullable(),
  /** The caller hosts it: gates the host controls. False signed out. */
  isViewerHost: z.boolean(),
  /** The host (or, on a house live break, the house) blocked the caller: show the removed screen. False signed out. */
  isViewerBlocked: z.boolean(),
  /** Its stats; null for anyone but its host. */
  stats: liveBreakStatsSchema.nullable(),
  /** The environment's maximum rate, for the host's rate control; null for anyone else and on a house live break. */
  maxRateBps: z.int().nullable(),
});
export type LiveBreakDetail = z.infer<typeof liveBreakDetailSchema>;

// ---- queue estimate --------------------------------------------------------------------------------

/** The caller's priority for a purchase made now (boxed's `LiveBreakViewerPriority`). */
export const liveBreakViewerPrioritySchema = z.object({
  status: z.enum(LIVE_BREAK_VIEWER_PRIORITY_STATUSES),
  /** The channel (login) to subscribe to; null when `unavailable`. */
  twitchChannel: z.string().nullable(),
  /**
   * The channel's name to show: its sponsored stream's staff name, else Twitch's display name,
   * else the login; null with `twitchChannel`.
   */
  displayName: z.string().nullable(),
});
export type LiveBreakViewerPriority = z.infer<typeof liveBreakViewerPrioritySchema>;

/**
 * The wait for one place in the queue (boxed's `LiveBreakQueueEstimate`): every entry ahead paced
 * as Auto-play runs it (`LIVE_BREAK_PRESENT_MS` + amount × `LIVE_BREAK_REVEAL_BEAT_MS` +
 * `LIVE_BREAK_RESULTS_HOLD_MS`), plus `LIVE_BREAK_ESTIMATE_BUFFER_MS` for what is on screen. Close
 * while Auto-play drives; a guide when a host clicks by hand.
 */
export const liveBreakQueueEstimateSchema = z.object({
  /** 1-based, counted like an entry's `position`. */
  position: z.int(),
  waitMs: z.int(),
  /** For a purchase made now by a signed-in caller; null with `entryId` and for a key alone. */
  priority: liveBreakViewerPrioritySchema.nullable(),
});
export type LiveBreakQueueEstimate = z.infer<typeof liveBreakQueueEstimateSchema>;

// ---- the host ----------------------------------------------------------------------------------------

/** One commission pool, in whole cents. */
export const liveBreakHostCommissionPoolSchema = z.object({
  /** What a claim would pay from the pool now: its unclaimed accruals, floored to cents. */
  unclaimedCents: cents,
  /** What claims have paid from it. */
  claimedCents: cents,
  /** `unclaimedCents + claimedCents`. */
  lifetimeCents: cents,
});
export type LiveBreakHostCommissionPool = z.infer<typeof liveBreakHostCommissionPoolSchema>;

/**
 * `GET /v1/me/live-break-host`: the caller as a host (boxed's `LiveBreakHost`), zeros before they
 * earn any. Rates are basis points; commission is whole cents, viewer and self kept apart.
 */
export const liveBreakHostSchema = z.object({
  /** May create a live break: false only while the Host Allow List is enforced and they are not on it. */
  canHost: z.boolean(),
  /** The rate their next live break starts at: their own, else the default, never above the maximum. */
  rateBps: z.int(),
  /** Their own stored rate; null while they follow the default. May be above `maxRateBps`. */
  ownRateBps: z.int().nullable(),
  defaultRateBps: z.int(),
  /** The highest rate any host may set; 0 turns host commission off (hide every rate control). */
  maxRateBps: z.int().max(LIVE_BREAK_MAX_RATE_BPS),
  /** What a new live break's minimum box price is offered as. */
  defaultMinBoxPriceCents: cents,
  commission: z.object({
    /** Earned on other shoppers' purchases. */
    viewer: liveBreakHostCommissionPoolSchema,
    /** Given back on the host's own purchases. */
    self: liveBreakHostCommissionPoolSchema,
  }),
  /** All-time figures across every live break they hosted. */
  totals: liveBreakStatsTotalsSchema.extend({ liveBreaksHosted: z.int() }),
  /** Their open live breaks, Draining ones included, newest first. */
  openLiveBreaks: z.array(liveBreakSchema),
});
export type LiveBreakHost = z.infer<typeof liveBreakHostSchema>;

/** A Block as its host reads it (boxed's `LiveBreakBlock`); who on staff made one never reaches a shopper. */
export const liveBreakBlockSchema = z.object({
  endUser: liveBreakProfileSchema,
  blockedAt: isoTime,
  blockedBy: z.enum(LIVE_BREAK_BLOCKED_BY),
});
export type LiveBreakBlock = z.infer<typeof liveBreakBlockSchema>;

/** One hosted live break as history lists it: what it was, its figures and its top pull. */
export const liveBreakHistoryItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  isPublic: z.boolean(),
  categories: z.array(boxCategorySchema),
  status: z.enum(LIVE_BREAK_STATUSES),
  closeReason: z.enum(LIVE_BREAK_CLOSE_REASONS).nullable(),
  /** The rate it was created with or last set to; purchases keep the rate they pinned. */
  commissionRateBps: z.int().nullable(),
  createdAt: isoTime,
  closedAt: nullableIsoTime,
  totals: liveBreakStatsTotalsSchema,
  /** Its most valuable pull; null before one. */
  topPull: liveBreakTopPullSchema.nullable(),
});
export type LiveBreakHistoryItem = z.infer<typeof liveBreakHistoryItemSchema>;

/** One hosted live break in full: history's fields and every stat (`liveBreakStatsSchema`). */
export const liveBreakHistoryDetailSchema = liveBreakHistoryItemSchema
  .omit({ totals: true, topPull: true })
  .extend({ stats: liveBreakStatsSchema });
export type LiveBreakHistoryDetail = z.infer<typeof liveBreakHistoryDetailSchema>;

/**
 * A playlist as a host copies it (boxed's `LiveBreakPlaylist`): only the boxes a live break could
 * sell today, never empty, priced at the read's rate. No live break's minimum box price applies.
 */
export const liveBreakPlaylistSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  boxes: z.array(liveBreakBoxSchema),
});
export type LiveBreakPlaylist = z.infer<typeof liveBreakPlaylistSchema>;

// ---- requests ---------------------------------------------------------------------------------------

const searchText = z.string().trim().max(LIVE_BREAK_SEARCH_MAX_LENGTH);
/** A comma-separated query value as its trimmed, non-empty parts. */
const commaList = (max: number, what: string) =>
  z
    .string()
    .transform((raw) => [
      ...new Set(
        raw
          .split(",")
          .map((part) => part.trim())
          .filter((part) => part.length > 0),
      ),
    ])
    .refine((parts) => parts.length <= max, `at most ${max} ${what}`);
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");

/** GET /v1/live-breaks: the directory. */
export const liveBreakDirectoryQuerySchema = z.object({
  /** A box category; a family matches its games. */
  category: boxCategorySchema.optional(),
  /** Matches the title or the host's username, case-insensitively. */
  q: searchText.optional(),
  cursor: z.string().optional(),
});
export type LiveBreakDirectoryQuery = z.infer<typeof liveBreakDirectoryQuerySchema>;

/** GET /v1/live-breaks/by-channel?logins=a,b */
export const liveBreakChannelQuerySchema = z.object({
  logins: commaList(LIVE_BREAK_CHANNEL_LOOKUP_MAX, "logins"),
});

/** GET /v1/live-breaks/{id}/queue */
export const liveBreakQueueQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(LIVE_BREAK_QUEUE_PAGE_MAX)
    .default(LIVE_BREAK_QUEUE_PAGE_DEFAULT),
});

/** GET /v1/live-breaks/{id}/estimate */
export const liveBreakEstimateQuerySchema = z.object({ entryId: z.string().min(1).optional() });

/** GET /v1/live-breaks/{id}/me/entries */
export const liveBreakMyEntriesQuerySchema = z.object({
  status: z.enum(LIVE_BREAK_MY_ENTRY_FILTERS).default("opened"),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** GET /v1/me/live-break-host/live-breaks */
export const liveBreakHistoryQuerySchema = z.object({ cursor: z.string().optional() });

/**
 * GET /v1/live-breaks/boxes: boxes priced as a live break would sell them (boxed's
 * `searchBoxesComplex` with `liveBreakPricing`). The rate is `rateBps` when given (an unsaved draft,
 * such as Room Settings previewing a new rate), else the named live break's, else the signed-in
 * caller's host rate, else the environment's default rate.
 */
export const liveBreakBoxSearchQuerySchema = z.object({
  /** Price at this live break's rate, hold outside boxes to its minimum box price, and mark its own boxes. */
  liveBreakId: z.string().min(1).optional(),
  rateBps: z.coerce.number().int().min(0).max(LIVE_BREAK_MAX_RATE_BPS).optional(),
  /** Matches the box's name, case-insensitively. */
  q: searchText.optional(),
  /** A box category; a family matches its games. */
  category: boxCategorySchema.optional(),
  /** Bounds on the box's own price (not its live-break price), inclusive. */
  minBoxPriceCents: z.coerce.number().int().min(0).optional(),
  maxBoxPriceCents: z.coerce.number().int().min(0).optional(),
  /** Read these boxes by id (comma-separated): each visible one, eligible or not, in this order. */
  ids: commaList(LIVE_BREAK_BOX_SEARCH_PAGE_MAX, "ids").optional(),
  /** Only the named live break's own boxes ("In live break"); needs `liveBreakId`. */
  onlySelected: queryBoolean.optional(),
  cursor: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(LIVE_BREAK_BOX_SEARCH_PAGE_MAX)
    .default(LIVE_BREAK_BOX_SEARCH_PAGE_DEFAULT),
});
export type LiveBreakBoxSearchQuery = z.infer<typeof liveBreakBoxSearchQuerySchema>;

/** GET /v1/live-breaks/playlists: priced like the box search, without `liveBreakId`. */
export const liveBreakPlaylistQuerySchema = z.object({
  rateBps: z.coerce.number().int().min(0).max(LIVE_BREAK_MAX_RATE_BPS).optional(),
});

/** POST /v1/live-breaks: a host opens a live break. Ranges are core's to refuse, naming the rule. */
export const createLiveBreakRequestSchema = z.object({
  title: z.string(),
  /** Listing only, as `LiveBreak.isPublic`. */
  isPublic: z.boolean(),
  /** Its live-break boxes, 1–`LIVE_BREAK_MAX_BOXES` after duplicates are dropped, in display order. */
  boxIds: z.array(z.string().min(1)).max(LIVE_BREAK_MAX_BOXES * 2),
  /** Omitted = false. */
  isRestrictedToLiveBreakBoxes: z.boolean().optional(),
  /** Omitted or null = no limit. */
  buyerQueueCap: z.int().nullable().optional(),
  /** Omitted = the environment's default minimum box price; 0 = none. */
  minBoxPriceCents: z.int().optional(),
  /** Omitted = 0. */
  openerDelayMs: z.int().optional(),
  /** The Twitch login it shows: the caller's own linked login only. Omitted, null or blank = none. */
  twitchChannel: z.string().max(64).nullable().optional(),
});
export type CreateLiveBreakRequest = z.infer<typeof createLiveBreakRequestSchema>;

/** PATCH /v1/live-breaks/{id}: the host's edit; an omitted key leaves the setting alone. */
export const updateLiveBreakRequestSchema = z.object({
  title: z.string().optional(),
  /**
   * The Twitch login it shows: the caller's own linked login only; null or blank clears it, and
   * re-sending the current one is no change.
   */
  twitchChannel: z.string().max(64).nullable().optional(),
  isPublic: z.boolean().optional(),
  isAcceptingPurchases: z.boolean().optional(),
  /** Off clears a pause. */
  isAutoMode: z.boolean().optional(),
  /** Needs Auto Mode on after the edit. */
  isPaused: z.boolean().optional(),
  openerDelayMs: z.int().optional(),
  /** null = no limit. */
  buyerQueueCap: z.int().nullable().optional(),
  isRestrictedToLiveBreakBoxes: z.boolean().optional(),
  /** Up to the environment's maximum rate; re-sending the current rate is no change. */
  commissionRateBps: z.int().nullable().optional(),
  minBoxPriceCents: z.int().optional(),
  /** Boxes to add after the current ones; a host never removes one. */
  addBoxIds: z.array(z.string().min(1)).max(LIVE_BREAK_MAX_BOXES).optional(),
});
export type UpdateLiveBreakRequest = z.infer<typeof updateLiveBreakRequestSchema>;

/** POST /v1/live-breaks/{id}/entries: buy 1–5 of one box into the queue; the host's Open Next with `isNext`. */
export const purchaseLiveBreakEntryRequestSchema = z.object({
  boxId: z.string().min(1),
  /** Omitted = 1. */
  amount: z.int().optional(),
  /** The live-break price of one box the buyer was shown; any other is `live_break_price_changed`. */
  expectedPriceCents: z.int(),
  clientSeed: clientSeedSchema,
  /** Host only: queue in the front lane, behind Up Next. Omitted = false. */
  isNext: z.boolean().optional(),
});
export type PurchaseLiveBreakEntryRequest = z.infer<typeof purchaseLiveBreakEntryRequestSchema>;

/** POST /v1/live-breaks/{id}/advance: the host's Next, or Open now from `presenting`. */
export const advanceLiveBreakRequestSchema = z.object({
  /** The phase the host's stage showed: a stale one is refused, so a double click never skips a step. */
  fromPhase: z.enum(LIVE_BREAK_REVEAL_PHASES),
  /** From idle, the Up Next the host saw (omit to take the head); past idle, the entry on stage, required. */
  entryId: z.string().min(1).optional(),
});
export type AdvanceLiveBreakRequest = z.infer<typeof advanceLiveBreakRequestSchema>;

/** PUT /v1/me/live-break-host/rate: the caller's own rate for live breaks they create from now on. */
export const setLiveBreakHostRateRequestSchema = z.object({
  /** Basis points, up to the environment's maximum; null = back to the default rate. */
  rateBps: z.int().nullable(),
});
export type SetLiveBreakHostRateRequest = z.infer<typeof setLiveBreakHostRateRequestSchema>;

/** POST /v1/me/live-break-host/blocks: Block one shopper from every live break of the caller's. */
export const blockLiveBreakShopperRequestSchema = z.object({ endUserId: z.string().min(1) });
export type BlockLiveBreakShopperRequest = z.infer<typeof blockLiveBreakShopperRequestSchema>;

// ---- responses -------------------------------------------------------------------------------------

const serverTime = isoTime.describe("The server's clock when the response was built.");

/** GET /v1/live-breaks: the directory, newest first. */
export const liveBreakListResponseSchema = z.object({
  data: z.array(liveBreakSchema),
  nextCursor: z.string().nullable(),
  serverTime,
});
export type LiveBreakListResponse = z.infer<typeof liveBreakListResponseSchema>;

/** GET /v1/live-breaks/by-channel */
export const liveBreakChannelListResponseSchema = z.object({
  data: z.array(liveBreakSchema),
  serverTime,
});
export type LiveBreakChannelListResponse = z.infer<typeof liveBreakChannelListResponseSchema>;

/** GET /v1/live-breaks/{id}, POST /v1/live-breaks, PATCH /v1/live-breaks/{id}. */
export const liveBreakResponseSchema = z.object({ liveBreak: liveBreakDetailSchema, serverTime });
export type LiveBreakResponse = z.infer<typeof liveBreakResponseSchema>;

/** POST /v1/live-breaks/{id}/advance: the stage after the step, and the entry it moved. */
export const liveBreakAdvanceResponseSchema = z.object({
  liveBreak: liveBreakDetailSchema,
  /** The entry the step put on stage or opened; on a clear to idle, the one whose results went. */
  entry: liveBreakEntrySchema.nullable(),
  serverTime,
});
export type LiveBreakAdvanceResponse = z.infer<typeof liveBreakAdvanceResponseSchema>;

/** POST /v1/live-breaks/{id}/end */
export const liveBreakEndResponseSchema = z.object({
  liveBreak: liveBreakDetailSchema,
  /** False when it was already Draining or closed: End is idempotent. */
  ended: z.boolean(),
  serverTime,
});
export type LiveBreakEndResponse = z.infer<typeof liveBreakEndResponseSchema>;

/** GET /v1/live-breaks/{id}/queue: queued entries in queue order, the one on stage included. */
export const liveBreakQueueResponseSchema = z.object({
  data: z.array(liveBreakEntrySchema),
  nextCursor: z.string().nullable(),
  queuedCount: z.int(),
  serverTime,
});
export type LiveBreakQueueResponse = z.infer<typeof liveBreakQueueResponseSchema>;

/** GET /v1/live-breaks/{id}/estimate: null once `entryId` has left the queue or is on stage. */
export const liveBreakEstimateResponseSchema = z.object({
  estimate: liveBreakQueueEstimateSchema.nullable(),
  serverTime,
});
export type LiveBreakEstimateResponse = z.infer<typeof liveBreakEstimateResponseSchema>;

/** POST /v1/live-breaks/{id}/entries: 201, or 200 with `replayed` for an Idempotency-Key replay. */
export const liveBreakPurchaseResponseSchema = z.object({
  entry: liveBreakMyEntrySchema,
  replayed: z.boolean(),
  serverTime,
});
export type LiveBreakPurchaseResponse = z.infer<typeof liveBreakPurchaseResponseSchema>;

/** POST /v1/live-breaks/{id}/entries/{entryId}/cancel: the entry, released. */
export const liveBreakCancelResponseSchema = z.object({
  entry: liveBreakMyEntrySchema,
  serverTime,
});
export type LiveBreakCancelResponse = z.infer<typeof liveBreakCancelResponseSchema>;

/** POST /v1/live-breaks/{id}/entries/{entryId}/pick */
export const liveBreakPickResponseSchema = z.object({
  entry: liveBreakEntrySchema,
  /** False when it was already in the front lane: nothing was written. */
  moved: z.boolean(),
  serverTime,
});
export type LiveBreakPickResponse = z.infer<typeof liveBreakPickResponseSchema>;

/** GET /v1/live-breaks/{id}/me/entries: opened newest-opened first, or queued in queue order. */
export const liveBreakMyEntriesResponseSchema = z.object({
  data: z.array(liveBreakMyEntrySchema),
  nextCursor: z.string().nullable(),
  serverTime,
});
export type LiveBreakMyEntriesResponse = z.infer<typeof liveBreakMyEntriesResponseSchema>;

/** GET /v1/me/live-breaks/entries: every queued entry of the caller's, newest live break first. */
export const liveBreakMyQueuedEntriesResponseSchema = z.object({
  data: z.array(liveBreakMyQueuedEntrySchema),
  serverTime,
});
export type LiveBreakMyQueuedEntriesResponse = z.infer<
  typeof liveBreakMyQueuedEntriesResponseSchema
>;

/** GET /v1/me/live-breaks/hosting: the caller's open live breaks as their host, newest first. */
export const liveBreakHostingResponseSchema = z.object({
  data: z.array(liveBreakSchema),
  serverTime,
});
export type LiveBreakHostingResponse = z.infer<typeof liveBreakHostingResponseSchema>;

/** GET /v1/live-breaks/boxes */
export const liveBreakBoxSearchResponseSchema = z.object({
  data: z.array(liveBreakBoxSchema),
  nextCursor: z.string().nullable(),
  /** The rate the boxes were priced at; null = box prices (a house live break). */
  rateBps: z.int().nullable(),
});
export type LiveBreakBoxSearchResponse = z.infer<typeof liveBreakBoxSearchResponseSchema>;

/** GET /v1/live-breaks/playlists: in staff order; a playlist with no eligible box is left out. */
export const liveBreakPlaylistListResponseSchema = z.object({
  data: z.array(liveBreakPlaylistSchema),
  /** The rate the boxes were priced at. */
  rateBps: z.int().nullable(),
});
export type LiveBreakPlaylistListResponse = z.infer<typeof liveBreakPlaylistListResponseSchema>;

/** GET /v1/me/live-break-host, PUT /v1/me/live-break-host/rate. */
export const liveBreakHostResponseSchema = z.object({ host: liveBreakHostSchema, serverTime });
export type LiveBreakHostResponse = z.infer<typeof liveBreakHostResponseSchema>;

/** GET /v1/me/live-break-host/box-stats: across every hosted live break, most viewer spend first. */
export const liveBreakHostBoxStatsResponseSchema = z.object({
  data: z.array(liveBreakBoxStatsSchema),
});
export type LiveBreakHostBoxStatsResponse = z.infer<typeof liveBreakHostBoxStatsResponseSchema>;

/** GET /v1/me/live-break-host/live-breaks: hosted, newest first, open and Draining ones included. */
export const liveBreakHistoryResponseSchema = z.object({
  data: z.array(liveBreakHistoryItemSchema),
  nextCursor: z.string().nullable(),
});
export type LiveBreakHistoryResponse = z.infer<typeof liveBreakHistoryResponseSchema>;

/** GET /v1/me/live-break-host/live-breaks/{id} */
export const liveBreakHistoryDetailResponseSchema = z.object({
  liveBreak: liveBreakHistoryDetailSchema,
});
export type LiveBreakHistoryDetailResponse = z.infer<typeof liveBreakHistoryDetailResponseSchema>;

/** GET /v1/me/live-break-host/blocks: newest first. */
export const liveBreakBlockListResponseSchema = z.object({ data: z.array(liveBreakBlockSchema) });
export type LiveBreakBlockListResponse = z.infer<typeof liveBreakBlockListResponseSchema>;

/** POST /v1/me/live-break-host/blocks */
export const liveBreakBlockResponseSchema = z.object({
  block: liveBreakBlockSchema,
  /** False when they were already blocked: the first Block stands. */
  created: z.boolean(),
  /** Their queued entries the Block released across the caller's open live breaks. */
  releasedCount: z.int(),
});
export type LiveBreakBlockResponse = z.infer<typeof liveBreakBlockResponseSchema>;

/** DELETE /v1/me/live-break-host/blocks/{endUserId} */
export const liveBreakUnblockResponseSchema = z.object({
  /** False when there was no Block to lift. */
  removed: z.boolean(),
});
export type LiveBreakUnblockResponse = z.infer<typeof liveBreakUnblockResponseSchema>;

// ---- realtime: what `liveBreakChannel(environmentId, liveBreakId)` carries (liveBreakPresence.ts) ---

const eventBase = { liveBreakId: z.string(), serverTime };

/** An edit, End, a host's return or lapse: the whole live break, no per-caller fields. */
export const liveBreakUpdatedEventSchema = z.object({
  type: z.literal("LIVE_BREAK_UPDATED"),
  ...eventBase,
  liveBreak: liveBreakSchema,
});

/** Every phase step, Unpresent included: the stage, the entry it moved, and the queue's size. */
export const liveBreakSequenceAdvancedEventSchema = z.object({
  type: z.literal("SEQUENCE_ADVANCED"),
  ...eventBase,
  sequence: liveBreakSequenceSchema,
  /** The entry the step put on stage or took off it; null for a step to idle with none. */
  entry: liveBreakEntrySchema.nullable(),
  queuedCount: z.int(),
});

/** A purchase queued an entry (an Open Next too). */
export const liveBreakEntryQueuedEventSchema = z.object({
  type: z.literal("ENTRY_QUEUED"),
  ...eventBase,
  entry: liveBreakEntrySchema,
  queuedCount: z.int(),
});

/** An entry's Reveal: the entry with its rolls. A delayed viewer's stage shows them later. */
export const liveBreakEntryOpenedEventSchema = z.object({
  type: z.literal("ENTRY_OPENED"),
  ...eventBase,
  entry: liveBreakEntrySchema,
  queuedCount: z.int(),
});

/** A cancel, a staff cancel or a Block released an entry (a close sends no per-entry events). */
export const liveBreakEntryReleasedEventSchema = z.object({
  type: z.literal("ENTRY_RELEASED"),
  ...eventBase,
  entry: liveBreakEntrySchema,
  queuedCount: z.int(),
});

/** A Pick moved an entry into the front lane, or Up Next was protected from a jump. */
export const liveBreakEntryPickedEventSchema = z.object({
  type: z.literal("ENTRY_PICKED"),
  ...eventBase,
  entry: liveBreakEntrySchema,
  queuedCount: z.int(),
});

/** A heartbeat changed the count or the roster, or a Block took a viewer out. */
export const liveBreakViewersChangedEventSchema = z.object({
  type: z.literal("VIEWERS_CHANGED"),
  ...eventBase,
  viewerCount: z.int(),
  /** Viewers who joined, as public profiles. */
  joined: z.array(liveBreakProfileSchema),
  departedEndUserIds: z.array(z.string()),
});

/** The close: every queued entry was released and its buyers' reveals claimed where possible. */
export const liveBreakClosedEventSchema = z.object({
  type: z.literal("LIVE_BREAK_CLOSED"),
  ...eventBase,
  liveBreak: liveBreakSchema,
});

/**
 * Every live-break event, one schema per type. Clients reconcile by refetch, so a missed event is a
 * delay, never a wrong state; an event whose phase is older than the cached one is dropped, and a
 * `queueKey` only ever decreases.
 */
export const liveBreakEventSchema = z.discriminatedUnion("type", [
  liveBreakUpdatedEventSchema,
  liveBreakSequenceAdvancedEventSchema,
  liveBreakEntryQueuedEventSchema,
  liveBreakEntryOpenedEventSchema,
  liveBreakEntryReleasedEventSchema,
  liveBreakEntryPickedEventSchema,
  liveBreakViewersChangedEventSchema,
  liveBreakClosedEventSchema,
]);
export type LiveBreakEventPayload = z.infer<typeof liveBreakEventSchema>;
export type LiveBreakEventType = LiveBreakEventPayload["type"];

// ---- pinned box versions: what a viewer's reel is drawn from ----------------------------------

/** Most pinned versions one read returns. */
export const LIVE_BREAK_BOX_VERSIONS_MAX = 50;

/**
 * A box version an entry of this live break pinned, with its drop table: the reel a viewer's stage
 * draws an entry's spin from. Versions never change, so a client caches each one by id. The shape
 * of a battle's pinned versions.
 */
export const liveBreakBoxVersionSchema = z.object({
  id: z.string(),
  boxId: z.string(),
  version: z.number(),
  name: z.string(),
  imageUrl: z.string(),
  priceCents: z.number().describe(CENTS),
  dropTable: z.array(dropTableItemSchema),
});
export type LiveBreakBoxVersion = z.infer<typeof liveBreakBoxVersionSchema>;

/** GET /v1/live-breaks/{id}/box-versions?ids=a,b: up to 50 version ids, comma separated. */
export const liveBreakBoxVersionsQuerySchema = z.object({
  ids: z
    .string()
    .transform((value) => [
      ...new Set(
        value
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean),
      ),
    ])
    .pipe(z.array(z.string().max(64)).min(1).max(LIVE_BREAK_BOX_VERSIONS_MAX)),
});

/** GET /v1/live-breaks/{id}/box-versions. */
export const liveBreakBoxVersionsResponseSchema = z.object({
  data: z.array(liveBreakBoxVersionSchema),
});
